import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/empty';
import 'rxjs/add/observable/fromPromise';
import 'rxjs/add/observable/merge';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { JiraPlanDataRestResponse } from '@atlassian/jira-software-backlog-uif-types/src/index.tsx';
import type { StoreFieldsActions } from '../../common/hooks/use-store-fields/types.tsx';
import { getCustomFilterFromUrlQuery } from '../../common/urls/index.tsx';
import { loadPlanDataService } from '../../services/load-plan-data/index.tsx';
import { transformPlanData } from '../../services/load-plan-data/transformers/index.tsx';
import {
	type LoadAppDataAction,
	LOAD_APP_DATA_REQUEST,
	loadAppDataFailure,
	setAppData,
} from '../../state/actions/app/index.tsx';
import {
	getEpicIssueType,
	getIsCompanyManaged,
	getRapidViewId,
} from '../../state/selectors/app/index.tsx';
import { getAppliedCustomFilters } from '../../state/selectors/filters/index.tsx';
import type { Dependencies, ActionsObservable, MiddlewareAPI } from '../../state/types.tsx';

const fetchData = ({
	store,
	prefetchedData,
	refreshData,
	storeFieldsActions,
}: {
	store: MiddlewareAPI;
	prefetchedData?: JiraPlanDataRestResponse | null;
	refreshData?: Promise<JiraPlanDataRestResponse> | null;
	storeFieldsActions?: StoreFieldsActions | null;
}) => {
	const state = store.getState();
	const isCompanyManaged = getIsCompanyManaged(state);
	const epicIssueType = getEpicIssueType(state);
	const activeQuickFilters =
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		getAppliedCustomFilters(state) ?? getCustomFilterFromUrlQuery(window.location);

	if (prefetchedData) {
		const refresh$: Observable<JiraPlanDataRestResponse> =
			refreshData && fg('indexeddb_cache_for_backlog')
				? Observable.fromPromise(refreshData)
				: Observable.empty();
		const refreshAction$ = refresh$.flatMap((data) =>
			Observable.of(setAppData(transformPlanData(data, isCompanyManaged, activeQuickFilters))),
		);

		return Observable.merge(
			Observable.of(
				setAppData(transformPlanData(prefetchedData, isCompanyManaged, activeQuickFilters)),
			),
			refreshAction$,
		);
	}

	const boardId = getRapidViewId(state);

	return loadPlanDataService({
		boardId,
		isCompanyManaged,
		activeQuickFilters,
		epicIssueType,
		storeFieldsActions,
	})
		.map(setAppData)
		.catch((error) => {
			log.safeErrorWithoutCustomerData(
				'backlog.load-plan-data.failure',
				'Failed to load plan data from REST',
				error,
			);
			return Observable.of(loadAppDataFailure());
		});
};

export const loadPlanDataEpic = (
	action$: ActionsObservable,
	store: MiddlewareAPI,
	dependencies?: Dependencies,
) =>
	action$.ofType(LOAD_APP_DATA_REQUEST).flatMap(({ payload }: LoadAppDataAction) =>
		fetchData({
			store,
			prefetchedData: fg('indexeddb_cache_for_backlog')
				? payload.prefetchCmpBacklogData?.result
				: payload.prefetchedJiraPlanRestData,
			refreshData: fg('indexeddb_cache_for_backlog')
				? payload.prefetchCmpBacklogData?.refresh
				: null,
			storeFieldsActions: payload.storeFieldsActions || dependencies?.storeFieldsActions,
		}),
	);
